import axios from "axios";

const TOKEN_KEY = "xyo_token";
const USER_KEY = "xyo_user";

const http = axios.create({
  baseURL: "/api/v1",
  timeout: 30000,
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (res) => res.data,
  (error) => {
    const status = error.response && error.response.status;
    const detail = (error.response && error.response.data && error.response.data.detail) || error.message;
    if (status === 401) {
      clearAuth();
    }
    return Promise.reject(new Error(typeof detail === "string" ? detail : "请求失败"));
  }
);

export function setAuth(token, user) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch (e) {
    return null;
  }
}

// 认证
export const authApi = {
  sendCode: (email) => http.post("/auth/email-code", { email }),
  register: (payload) => http.post("/auth/register", payload),
  login: (email, password) => http.post("/auth/login", { email, password }),
  me: () => http.get("/auth/me"),
  sendResetCode: (email) => http.post("/auth/reset-code", { email }),
  resetPassword: (payload) => http.post("/auth/reset-password", payload),
  updateProfile: (payload) => http.put("/auth/profile", typeof payload === "string" ? { display_name: payload } : payload),
  changePassword: (current_password, new_password) => http.post("/auth/change-password", { current_password, new_password }),
  deactivate: (password) => http.post("/auth/deactivate", { password }),
};

// 仪表盘
export const dashboardApi = {
  get: () => http.get("/dashboard"),
};

// 支付
export const paymentApi = {
  precreate: (planCode, period, channel = "alipay", paymentMode = "qr") => {
    const normalizedChannel = channel === "wechat" ? "wechat" : "alipay";
    return http.post(`/payments/${normalizedChannel}/precreate`, {
      plan_code: planCode,
      period,
      channel: normalizedChannel,
      payment_mode: normalizedChannel === "alipay" && paymentMode === "wap" ? "wap" : "qr",
    });
  },
  orderStatus: (outTradeNo) => http.get(`/payments/orders/${outTradeNo}`),
  cancelOrder: (outTradeNo) => http.post(`/payments/orders/${encodeURIComponent(outTradeNo)}/cancel`),
  orders: () => http.get("/payments/orders"),
};

// 角色卡
export const roleCardApi = {
  list: () => http.get("/role-cards"),
  create: (payload) => http.post("/role-cards", payload),
  update: (id, payload) => http.put(`/role-cards/${id}`, payload),
  remove: (id) => http.delete(`/role-cards/${id}`),
  uploadReference: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/role-cards/reference-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  createCompanion: (id, payload) => http.post(`/role-cards/${id}/companions`, payload),
  generatePhoto: (payload) => http.post("/role-cards/generate-photo", payload),
  expandPersona: (payload) => http.post("/role-cards/expand-persona", payload),
};

// 图像工作台 / 图像库（生图任务异步执行，前端轮询状态）
export const imageStudioApi = {
  createJob: (payload) => http.post("/image-studio/jobs", payload),
  getJob: (jobId) => http.get(`/image-studio/jobs/${jobId}`),
  listJobs: () => http.get("/image-studio/jobs"),
  listAssets: () => http.get("/image-studio/assets"),
  uploadAsset: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/image-studio/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

// 伴侣
export const companionApi = {
  list: () => http.get("/companions"),
  editorConfig: () => http.get("/companions/editor-config"),
  create: (payload) => http.post("/companions", payload),
  detail: (id) => http.get(`/companions/${id}`),
  update: (id, payload) => http.put(`/companions/${id}`, payload),
  remove: (id) => http.delete(`/companions/${id}`),
  management: (id) => http.get(`/companions/${id}/management`),
  updatePresentation: (id, payload) => http.put(`/companions/${id}/presentation`, payload),
  chat: (id, sessionKey = "") => http.get(`/companions/${id}/chat`, { params: { session_key: sessionKey } }),
  sendChat: (id, text, imageUrl, relayToWechat, sessionKey = "", sourceMessageId = "") =>
    http.post(
      `/companions/${id}/chat`,
      {
        text,
        source_message_id: sourceMessageId,
        image_url: imageUrl || "",
        relay_to_wechat: !!relayToWechat,
        session_key: sessionKey,
      }
    ),
  turn: (id, turnId) => http.get(`/companions/${id}/turns/${encodeURIComponent(turnId)}`),
  chatSessions: (id) => http.get(`/companions/${id}/chat-sessions`),
  createChatSession: (id, displayName) => http.post(`/companions/${id}/chat-sessions`, { display_name: displayName }),
  removeChatSession: (id, key) => http.delete(`/companions/${id}/chat-sessions/${key}`),
  wechatSync: (id, afterIndex = -1, limit = 50) =>
    http.get(`/companions/${id}/wechat-sync`, { params: { after_index: afterIndex, limit } }),
  resetMemory: (id, sessionId) => http.post(`/companions/${id}/sessions/${sessionId}/memory/reset`),
  updateIntimacy: (id, sessionId, payload) =>
    http.put(`/companions/${id}/sessions/${encodeURIComponent(sessionId)}/intimacy`, payload),
  uploadChatImage: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post(`/companions/${id}/chat/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  scheduledTasks: (id) => http.get(`/companions/${id}/scheduled-tasks`),
  createTask: (id, payload) => http.post(`/companions/${id}/scheduled-tasks`, payload),
  cancelTask: (id, taskId) => http.delete(`/companions/${id}/scheduled-tasks/${taskId}`),
  wechatBindings: (id) => http.get(`/companions/${id}/wechat-bindings`),
  createOwnerPeerClaim: (id) => http.post(`/companions/${id}/owner-peer-claim`),
  bindWechat: (id, wechatAccountId, isDefault = false) =>
    http.post(`/companions/${id}/wechat-bindings`, { wechat_account_id: wechatAccountId, is_default: isDefault }),
  createWechatBindJob: (id) => http.post(`/companions/${id}/wechat-bind-jobs`),
  unbindWechat: (id, wechatAccountId) => http.delete(`/companions/${id}/wechat-bindings/${encodeURIComponent(wechatAccountId)}`),
  uploadAsset: (id, kind, file, note = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", note);
    return http.post(`/companions/${id}/assets/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  deleteAsset: (id, assetId) => http.delete(`/companions/${id}/assets/${assetId}`),
};

// 微信号
export const wechatApi = {
  list: () => http.get("/wechat-accounts"),
  remove: (wechatAccountId) => http.delete(`/wechat-accounts/${encodeURIComponent(wechatAccountId)}`),
  detail: (wechatAccountId) => http.get(`/wechat-accounts/${encodeURIComponent(wechatAccountId)}`),
  createBindJob: (companionId = 0) => http.post("/wechat-accounts/bind-jobs", null, { params: { companion_id: companionId } }),
  getBindJob: (jobId) => http.get(`/wechat-accounts/bind-jobs/${jobId}`),
  confirmBindReplace: (jobId, wechatAccountId) =>
    http.post(`/wechat-accounts/bind-jobs/${jobId}/confirm-replace`, { wechat_account_id: wechatAccountId || "" }),
};

// 角色卡分享 / 代聊授权
export const shareApi = {
  create: (roleCardId, payload) => http.post(`/role-cards/${roleCardId}/shares`, payload),
  myShares: () => http.get("/role-card-shares"),
  revoke: (shareId) => http.delete(`/role-card-shares/${shareId}`),
  publicView: (token) => http.get(`/public/role-card-shares/${token}`),
  import: (token) => http.post(`/role-card-shares/${token}/import`),
  myImports: () => http.get("/role-card-imports"),
  grant: (companionId, payload) => http.post(`/companions/${companionId}/conversation-grants`, payload),
  myGrants: () => http.get("/conversation-grants"),
  revokeGrant: (companionId, grantId) => http.delete(`/companions/${companionId}/conversation-grants/${grantId}`),
};

export const sharedConvApi = {
  list: () => http.get("/shared-conversations"),
  messages: (grantId, limit = 80) => http.get(`/shared-conversations/${grantId}/messages`, { params: { limit } }),
  send: (grantId, content) => http.post(`/shared-conversations/${grantId}/messages`, { content }),
};

// 管理后台
export const adminApi = {
  users: () => http.get("/admin/users"),
  paymentOrders: (params = {}) => http.get("/admin/payment-orders", { params }),
  paymentOrderStats: (params = {}) => http.get("/admin/payment-orders/stats", { params }),
  paymentOrderDetail: (outTradeNo) => http.get(`/admin/payment-orders/${encodeURIComponent(outTradeNo)}`),
  updateQuota: (id, token_quota) => http.patch(`/admin/users/${id}/quota`, { token_quota }),
  updateMessageQuota: (id, message_limit) => http.patch(`/admin/users/${id}/message-quota`, { message_limit }),
  setUserActive: (id, is_active) => http.patch(`/admin/users/${id}/active`, { is_active }),
  subscriptionPlans: () => http.get("/admin/subscription-plans"),
  updateSubscriptionPlan: (planCode, payload) => http.put(`/admin/subscription-plans/${encodeURIComponent(planCode)}`, payload),
  updateUserSubscription: (id, plan_code) => http.put(`/admin/users/${id}/subscription`, { plan_code }),
  refreshUserSubscription: (id) => http.post(`/admin/users/${id}/subscription/refresh`),
  userRoleCards: (id) => http.get(`/admin/users/${id}/role-cards`),
  systemRoleCards: () => http.get("/admin/system-role-cards"),
  updateSystemRoleCardAccessMode: (id, access_mode) =>
    http.patch(`/admin/system-role-cards/${id}/access-mode`, { access_mode }),
  roleCardAccessGrants: (id) => http.get(`/admin/system-role-cards/${id}/access-grants`),
  grantRoleCardAccess: (id, user_id, note = "") =>
    http.post(`/admin/system-role-cards/${id}/access-grants`, { user_id, note }),
  revokeRoleCardAccess: (id, userId) => http.delete(`/admin/system-role-cards/${id}/access-grants/${userId}`),
  companions: () => http.get("/admin/companions"),
  updateCompanion: (id, payload) => http.put(`/admin/companions/${id}`, payload),
  unbindCompanion: (id) => http.post(`/admin/companions/${id}/unbind`),
  deleteCompanion: (id) => http.delete(`/admin/companions/${id}`),
  auditLogs: () => http.get("/admin/audit-logs"),
  globalSystemPrompt: () => http.get("/admin/global-system-prompt"),
  updateGlobalSystemPrompt: (prompt) => http.put("/admin/global-system-prompt", { prompt }),
  gatewayConfig: () => http.get("/admin/weixin-gateway/config"),
  updateGatewayConfig: (payload) => http.put("/admin/weixin-gateway/config", payload),
  smtp: () => http.get("/admin/smtp"),
  updateSmtp: (payload) => http.put("/admin/smtp", payload),
  testSmtp: (email) => http.post("/admin/smtp/test", { email }),
  gatewayStatus: () => http.get("/admin/weixin-gateway/status"),
  // 统一提示词管理
  listPrompts: () => http.get("/admin/prompts"),
  updatePrompt: (key, value) => http.put(`/admin/prompts/${encodeURIComponent(key)}`, { value }),
  resetPrompt: (key) => http.delete(`/admin/prompts/${encodeURIComponent(key)}`),
  exportPrompts: () => http.get("/admin/prompts/export"),
  importPrompts: (overrides) => http.post("/admin/prompts/import", { overrides }),
};

export default http;
